import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { utils } from '../../utils';
import { usePermissions } from '../../hooks';
import { SystemModule } from '../../types/permissions';
import { listarClientes, deletarCliente, Cliente } from '../../services/clientes';
import FilterPanel from '../../components/Common/FilterPanel';
import ResultsCounter from '../../components/Common/ResultsCounter';

const ClientesList: React.FC = () => {
  const navigate = useNavigate();
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 0 });

  const fetchClientes = useCallback(async (page = 1) => {
    setIsLoading(true);
    try {
      const result = await listarClientes({
        page,
        limit: 20,
        busca: filter || undefined,
        ativo: statusFilter || undefined
      });

      if (result.success) {
        setClientes(result.clientes);
        setPagination(result.pagination);
      }
    } catch (error: any) {
      console.error('Erro ao carregar clientes:', error);
      utils.showNotification(error.message || 'Erro ao carregar clientes', { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [filter, statusFilter]);

  useEffect(() => {
    fetchClientes();
  }, [fetchClientes]);

  const handleDelete = async (id: number, nome: string) => {
    // eslint-disable-next-line no-restricted-globals
    if (confirm(`Tem certeza que deseja excluir o cliente "${nome}"?`)) {
      try {
        await deletarCliente(id);
        utils.showNotification('Cliente excluído com sucesso!', { type: 'success' });
        fetchClientes(pagination.page);
      } catch (error: any) {
        utils.showNotification(error.message || 'Erro ao excluir cliente', { type: 'error' });
      }
    }
  };

  const formatCNPJ = (cnpj: string): string => {
    if (!cnpj) return '-';
    const cleaned = cnpj.replace(/\D/g, '');
    if (cleaned.length === 14) {
      return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    return cnpj;
  };

  if (isLoading && clientes.length === 0) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1><i className="fas fa-building"></i> Clientes</h1>
          <div style={{ display: 'flex', gap: '12px' }}>
            {canCreate(SystemModule.CONFERENCIA_CARGA) && (
              <button
                className="btn btn-primary"
                onClick={() => navigate('/clientes/novo')}
              >
                <i className="fas fa-plus"></i> Novo Cliente
              </button>
            )}
          </div>
        </div>
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <div className="loading-spinner-large"></div>
          <p>Carregando clientes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1><i className="fas fa-building"></i> Clientes</h1>
        <div style={{ display: 'flex', gap: '12px' }}>
          {canCreate(SystemModule.CONFERENCIA_CARGA) && (
            <button
              className="btn btn-primary"
              onClick={() => navigate('/clientes/novo')}
            >
              <i className="fas fa-plus"></i> Novo Cliente
            </button>
          )}
        </div>
      </div>

      <div className="page-content">
        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2>Cadastro de Clientes</h2>
            <FilterPanel
              fields={[
                {
                  type: 'text',
                  label: 'Buscar',
                  value: filter,
                  onChange: setFilter,
                  placeholder: 'Razão Social, CNPJ...'
                },
                {
                  type: 'select',
                  label: 'Status',
                  value: statusFilter,
                  onChange: setStatusFilter,
                  options: [
                    { value: '', label: 'Todos' },
                    { value: 'true', label: 'Ativo' },
                    { value: 'false', label: 'Inativo' }
                  ]
                }
              ]}
              onFilter={() => fetchClientes(1)}
              onClear={() => {
                setFilter('');
                setStatusFilter('');
              }}
              showFilters={showFilters}
              onToggleFilters={() => setShowFilters(!showFilters)}
              title="Filtros de Clientes"
            />
          </div>

          <div className="card-body">
            {clientes.length > 0 && (
              <ResultsCounter
                displayedItems={clientes.length}
                totalItems={pagination.total}
                isLoading={isLoading}
                label="clientes"
              />
            )}

            {clientes.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-building empty-state-icon"></i>
                <h3>Nenhum cliente encontrado</h3>
                <p>{filter || statusFilter ? 'Tente ajustar os filtros de busca.' : 'Cadastre os primeiros clientes da sua empresa.'}</p>
                {canCreate(SystemModule.CONFERENCIA_CARGA) && (
                  <button
                    className="btn btn-primary"
                    onClick={() => navigate('/clientes/novo')}
                  >
                    Cadastrar Primeiro Cliente
                  </button>
                )}
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Razão Social</th>
                      <th>CNPJ</th>
                      <th>Cidade/UF</th>
                      <th>Telefone</th>
                      <th>Etiquetas</th>
                      <th>Áreas</th>
                      <th>Status</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientes.map(cliente => (
                      <tr key={cliente.id}>
                        <td>
                          <strong>{cliente.razao_social}</strong>
                          {cliente.nome_fantasia && (
                            <div style={{ fontSize: '0.8rem', color: '#7f8c8d' }}>
                              {cliente.nome_fantasia}
                            </div>
                          )}
                        </td>
                        <td>{formatCNPJ(cliente.cnpj)}</td>
                        <td>
                          {cliente.endereco_cidade && cliente.endereco_uf
                            ? `${cliente.endereco_cidade}/${cliente.endereco_uf}`
                            : '-'}
                        </td>
                        <td>{cliente.telefone || cliente.celular || '-'}</td>
                        <td>
                          <span className="status-badge status-info">
                            {cliente.total_etiquetas || 0} campos
                          </span>
                        </td>
                        <td>
                          {(cliente.total_areas_atendidas || 0) > 0 ? (
                            <span className="status-badge status-active" style={{ fontSize: '0.75rem' }}>
                              {cliente.total_areas_atendidas} áreas
                            </span>
                          ) : (
                            <span style={{ color: '#95a5a6', fontSize: '0.8rem' }}>Todas</span>
                          )}
                        </td>
                        <td>
                          <span className={`status-badge ${cliente.ativo ? 'status-active' : 'status-inactive'}`}>
                            {cliente.ativo ? 'Ativo' : 'Inativo'}
                          </span>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="btn-view"
                              onClick={() => navigate(`/clientes/visualizar/${cliente.id}`)}
                              title="Visualizar"
                            >
                              <i className="fas fa-eye"></i>
                            </button>
                            {canUpdate(SystemModule.CONFERENCIA_CARGA) && (
                              <button
                                className="btn-edit"
                                onClick={() => navigate(`/clientes/editar/${cliente.id}`)}
                                title="Editar"
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                            )}
                            {canDelete(SystemModule.CONFERENCIA_CARGA) && (
                              <button
                                className="btn-delete"
                                onClick={() => handleDelete(cliente.id, cliente.razao_social)}
                                title="Excluir"
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Paginação */}
                {pagination.totalPages > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '20px' }}>
                    <button
                      className="btn btn-secondary"
                      disabled={!pagination.page || pagination.page <= 1}
                      onClick={() => fetchClientes(pagination.page - 1)}
                    >
                      Anterior
                    </button>
                    <span style={{ display: 'flex', alignItems: 'center', padding: '0 12px', color: '#7f8c8d' }}>
                      Página {pagination.page} de {pagination.totalPages}
                    </span>
                    <button
                      className="btn btn-secondary"
                      disabled={pagination.page >= pagination.totalPages}
                      onClick={() => fetchClientes(pagination.page + 1)}
                    >
                      Próxima
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default ClientesList;
